import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClone, faCheck } from "@fortawesome/pro-solid-svg-icons";
import { useEffect, useState } from "react";
import { useCopyToClipboard } from "~/components/CopyToClipboard";
import { cn } from "~/lib/utils";

// // // //

/**
 * CopyButton
 * Small inline button that copies `value` to the clipboard and briefly shows a
 * checkmark. Optionally renders `label` (e.g. a truncated client ID) before the
 * icon.
 */
export function CopyButton(props: {
    value: string;
    label?: string;
    className?: string;
    ariaLabel?: string;
}) {
    const { value, label, className, ariaLabel } = props;
    const [copied, setCopied] = useState(false);
    const [copyToClipboard] = useCopyToClipboard(() => setCopied(true));

    useEffect(() => {
        if (!copied) return;
        const timeout = setTimeout(() => setCopied(false), 1200);
        return () => clearTimeout(timeout);
    }, [copied]);

    return (
        <button
            type="button"
            aria-label={ariaLabel ?? "Copy to clipboard"}
            onClick={() => copyToClipboard(value)}
            className={cn(
                "group inline-flex items-center gap-2 rounded-md border bg-muted/40 py-1 pl-2.5 pr-2 font-mono text-xs text-foreground/80 transition-colors hover:border-primary/40 hover:bg-muted",
                className,
            )}
        >
            {label && <span className="truncate">{label}</span>}
            <FontAwesomeIcon
                icon={copied ? faCheck : faClone}
                className={cn("h-3", copied && "text-green-600")}
            />
        </button>
    );
}
